/**
 * Хуки данных меню F&B и фолио гостя (UC-G.09 / UC-G.13).
 *
 * Меню = F&B-категории каталога услуг (GET /v1/service-catalog), поэтому
 * переиспользуем тот же query, что и витрина каталога, и фильтруем
 * категории по isFnbCategoryCode. Заказ = POST /v1/requests (одна заявка
 * на позицию корзины) — серверной корзины нет.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createServiceRequest,
  getServiceCatalog,
  listManagerRequests,
  listMyFnbRequests,
} from '@hsc/api-client';
import {
  isFnbCategoryCode,
  localizeText,
  type CatalogViewCategory,
  type CatalogViewResponse,
  type GuestRequestsListResponse,
  type ManagerRequest,
  type ServiceRequestCreateInput,
  type ServiceRequestCreatedResponse,
} from '@hsc/types';

/** Витрина каталога (тот же ключ, что и features/catalog — общий кэш). */
export function useFnbCatalog(locale: string) {
  return useQuery<CatalogViewResponse, Error, CatalogViewCategory[]>({
    queryKey: ['service-catalog', locale],
    queryFn: () => getServiceCatalog(locale),
    // Отбираем только F&B-категории и только те, где есть позиции.
    select: (data) =>
      data.categories.filter((c) => isFnbCategoryCode(c.code) && c.items.length > 0),
  });
}

/** Лента собственных заявок гостя (его заказы и начисления). */
export function useMyRequests(limit = 100) {
  return useQuery<GuestRequestsListResponse>({
    queryKey: ['me-requests', limit],
    queryFn: () => listMyFnbRequests(limit),
  });
}

/** Создание заявки-заказа (POST /v1/requests), с инвалидацией ленты гостя. */
export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation<ServiceRequestCreatedResponse, Error, ServiceRequestCreateInput>({
    mutationFn: createServiceRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

/* =====================================================================
 * Менеджерское управление меню (CRUD) — admin-каталог F&B.
 *
 * Менеджер/владелец управляет позициями меню через admin-CRUD каталога
 * услуг, отфильтрованный до F&B-категорий (та же эвристика isFnbCategoryCode,
 * что и на витрине гостя). Server-state — только через React Query; все
 * мутации инвалидируют и админ-список меню, и витрину гостя.
 * ===================================================================== */
import {
  type AdminCategory,
  type AdminItem,
  type CategoryBody,
  type ItemBody,
  createAdminCategory,
  createAdminItem,
  deleteAdminCategory,
  deleteAdminItem,
  listAdminCategories,
  listAdminItems,
  setAdminCategoryActive,
  setAdminItemActive,
  updateAdminCategory,
  updateAdminItem,
} from './menu-admin.js';

const MENU_ADMIN_KEY = ['menu-admin'] as const;

/** F&B-категория меню с подгруженными позициями (для менеджера). */
export interface MenuAdminCategory extends AdminCategory {
  items: AdminItem[];
}

/**
 * Полный админ-снимок меню: F&B-категории + их позиции. Категории берём
 * из admin-каталога и отбираем по isFnbCategoryCode (кухня/бар/room_service/
 * minibar — то же, что видит гость), затем для каждой грузим позиции.
 */
export function useMenuAdmin() {
  return useQuery<MenuAdminCategory[]>({
    queryKey: MENU_ADMIN_KEY,
    queryFn: async () => {
      const cats = await listAdminCategories();
      const fnb = cats
        .filter((c) => c.department_code === 'f_and_b' || isFnbCategoryCode(c.code))
        .sort((a, b) => a.sort_order - b.sort_order);
      const withItems = await Promise.all(
        fnb.map(async (c) => ({
          ...c,
          items: (await listAdminItems(c.id)).sort((a, b) => a.sort_order - b.sort_order),
        })),
      );
      return withItems;
    },
    staleTime: 15_000,
  });
}

/** Создание/обновление/удаление категории меню. */
export function useMenuCategoryMutations() {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: MENU_ADMIN_KEY });
    void qc.invalidateQueries({ queryKey: ['service-catalog'] });
  };
  const create = useMutation<AdminCategory, Error, CategoryBody>({
    mutationFn: (body) => createAdminCategory(body),
    onSuccess: invalidate,
  });
  const update = useMutation<AdminCategory, Error, { id: string; body: CategoryBody }>({
    mutationFn: ({ id, body }) => updateAdminCategory(id, body),
    onSuccess: invalidate,
  });
  const remove = useMutation<void, Error, string>({
    mutationFn: (id) => deleteAdminCategory(id),
    onSuccess: invalidate,
  });
  // Активация/архивирование категории (чекбокс «Активна» §9.2, #74).
  const setActive = useMutation<AdminCategory, Error, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) => setAdminCategoryActive(id, active),
    onSuccess: invalidate,
  });
  // Bulk-архивирование выбранных категорий (§9.2, #74): по каждой —
  // deactivate. Идемпотентно (повторный deactivate безопасен).
  const archiveMany = useMutation<void, Error, string[]>({
    mutationFn: async (ids) => {
      await Promise.all(ids.map((id) => setAdminCategoryActive(id, false)));
    },
    onSuccess: invalidate,
  });
  // Перестановка категорий drag-sort: для каждой затронутой категории —
  // PUT с новым sort_order (полное тело, как ждёт handler). Body собираем из
  // текущей проекции, чтобы не потерять name/icon/cover/availability.
  const reorder = useMutation<void, Error, AdminCategory[]>({
    mutationFn: async (ordered) => {
      await Promise.all(
        ordered.map((c, idx) => {
          const body: CategoryBody = {
            name: c.name,
            department_code: c.department_code,
            description: c.description ?? {},
            icon: c.icon ?? '',
            cover_url: c.cover_url ?? '',
            availability: c.availability ?? '',
            sort_order: (idx + 1) * 10,
          };
          if (c.sla_response_min !== undefined) body.sla_response_min = c.sla_response_min;
          if (c.sla_resolution_min !== undefined) body.sla_resolution_min = c.sla_resolution_min;
          return updateAdminCategory(c.id, body);
        }),
      );
    },
    onSuccess: invalidate,
  });
  return { create, update, remove, reorder, setActive, archiveMany };
}

/** Создание/обновление/удаление позиции меню. */
export function useMenuItemMutations() {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: MENU_ADMIN_KEY });
    void qc.invalidateQueries({ queryKey: ['service-catalog'] });
  };
  const create = useMutation<AdminItem, Error, ItemBody>({
    mutationFn: (body) => createAdminItem(body),
    onSuccess: invalidate,
  });
  const update = useMutation<AdminItem, Error, { id: string; body: ItemBody }>({
    mutationFn: ({ id, body }) => updateAdminItem(id, body),
    onSuccess: invalidate,
  });
  const remove = useMutation<void, Error, string>({
    mutationFn: (id) => deleteAdminItem(id),
    onSuccess: invalidate,
  });
  // Активация/архивирование позиции (chip «активна»/«в архиве» §9.3).
  const setActive = useMutation<AdminItem, Error, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) => setAdminItemActive(id, active),
    onSuccess: invalidate,
  });
  // Bulk-архивирование выбранных позиций (§9.3 «Архивировать»).
  const archiveMany = useMutation<void, Error, string[]>({
    mutationFn: async (ids) => {
      await Promise.all(ids.map((id) => setAdminItemActive(id, false)));
    },
    onSuccess: invalidate,
  });
  // Bulk-изменение цены на процент (§9.3 «Изменить цену %»). Для каждой
  // позиции с фиксированной ценой пересобираем полное тело PUT с новой ценой
  // (на ±percent, округление до минорной единицы, не ниже нуля).
  const repriceMany = useMutation<void, Error, { items: AdminItem[]; percent: number }>({
    mutationFn: async ({ items, percent }) => {
      const factor = 1 + percent / 100;
      await Promise.all(
        items
          .filter((it) => it.price_mode === 'fixed' && it.price_minor !== undefined)
          .map((it) => {
            const next = Math.max(0, Math.round((it.price_minor ?? 0) * factor));
            const body: ItemBody = {
              name: it.name,
              description: it.description ?? {},
              is_paid: it.is_paid,
              price_mode: it.price_mode,
              price_minor: next,
              currency: it.currency ?? 'RUB',
              show_price: it.show_price,
              image_url: it.image_url ?? null,
              cost_minor: it.cost_minor ?? null,
              allergens: it.allergens ?? [],
              sort_order: it.sort_order,
            };
            return updateAdminItem(it.id, body);
          }),
      );
    },
    onSuccess: invalidate,
  });
  return { create, update, remove, setActive, archiveMany, repriceMany };
}

/* =====================================================================
 * Обзор меню (design §9.1): KPI + хиты недели.
 *
 * Позиций/категорий — из admin-снимка меню (useMenuAdmin). Заказы сегодня /
 * выручка нед. / хиты — агрегируем на клиенте из ленты заявок отеля
 * (GET /v1/admin/requests, тот же источник, что и очередь F&B). Цену для
 * выручки берём из позиции меню по item_id (в заявке цены нет). Если данных
 * нет — отдаём 0/пусто (graceful, без выдуманных чисел).
 * ===================================================================== */

/** Один пункт «хитов недели»: название позиции + число заказов. */
export interface MenuHit {
  itemId: string;
  name: string;
  orders: number;
  /** true — лидер по выручке (orders×price), помечается чипом. */
  revenueLeader?: boolean;
}

/** Сводка обзора меню. */
export interface MenuOverview {
  items: number;
  categories: number;
  ordersToday: number;
  ordersActive: number;
  revenueWeekMinor: number;
  currency: string;
  hits: MenuHit[];
  /** Категории, у которых 0 позиций (для подсказки/пустых состояний). */
  emptyCategories: number;
  lowStock: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ACTIVE_STATES = new Set(['created', 'assigned', 'escalated', 'in_progress']);

/** Считает обзор из снимка меню и ленты заявок отеля. */
export function useMenuOverview() {
  const menu = useMenuAdmin();
  const requests = useQuery({
    queryKey: ['menu-overview', 'admin-requests'],
    queryFn: () => listManagerRequests({ limit: 200 }),
    staleTime: 15_000,
  });

  const data = useMemo<MenuOverview | undefined>(() => {
    const cats = menu.data;
    if (cats === undefined) return undefined;
    // Карта позиций меню для резолва цены/имени по item_id заявки.
    const itemById = new Map(cats.flatMap((c) => c.items).map((it) => [it.id, it]));
    const fnbCatCodes = new Set(cats.map((c) => c.code));
    const reqs: ManagerRequest[] = requests.data?.items ?? [];
    const isFnbReq = (r: ManagerRequest): boolean =>
      fnbCatCodes.has(r.category) || (r.item_id !== undefined && itemById.has(r.item_id));

    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    let ordersToday = 0;
    let ordersActive = 0;
    let revenueWeekMinor = 0;
    let currency = 'RUB';
    const orderCount = new Map<string, number>();

    for (const r of reqs) {
      if (!isFnbReq(r)) continue;
      const created = Date.parse(r.created_at);
      if (!Number.isNaN(created) && created >= startOfToday.getTime()) ordersToday += 1;
      if (ACTIVE_STATES.has(r.state)) ordersActive += 1;
      const it = r.item_id !== undefined ? itemById.get(r.item_id) : undefined;
      if (it !== undefined) {
        orderCount.set(it.id, (orderCount.get(it.id) ?? 0) + 1);
        if (!Number.isNaN(created) && now - created <= 7 * DAY_MS && it.price_minor !== undefined) {
          revenueWeekMinor += it.price_minor;
          if (it.currency !== undefined) currency = it.currency;
        }
      }
    }

    const hits: MenuHit[] = [...orderCount.entries()]
      .map(([itemId, orders]) => {
        const it = itemById.get(itemId);
        return {
          itemId,
          name: it ? localizeText(it.name, 'ru') || it.code : itemId,
          orders,
        };
      })
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);
    // Лидер по выручке: orders × price среди хитов.
    let leaderId: string | undefined;
    let leaderRevenue = -1;
    for (const h of hits) {
      const it = itemById.get(h.itemId);
      const rev = h.orders * (it?.price_minor ?? 0);
      if (rev > leaderRevenue) {
        leaderRevenue = rev;
        leaderId = h.itemId;
      }
    }
    const hitsMarked = hits.map((h) => (h.itemId === leaderId ? { ...h, revenueLeader: true } : h));

    return {
      items: cats.reduce((s, c) => s + c.items.length, 0),
      categories: cats.length,
      ordersToday,
      ordersActive,
      revenueWeekMinor,
      currency,
      hits: hitsMarked,
      emptyCategories: cats.filter((c) => c.items.length === 0).length,
      lowStock: 0,
    };
  }, [menu.data, requests.data]);

  return {
    data,
    isLoading: menu.isLoading,
    isError: menu.isError,
    refetch: () => {
      void menu.refetch();
      void requests.refetch();
    },
  };
}
